import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { TransactionFollowUpMessage, TransactionFollowUpStatus } from '../types/entities';
import { hasUsableClientPhone } from '../utils/clientPhone';
import {
  getMessagesClientsConfiguration,
  MessagesClientsTemplatesConfiguration,
} from '../types/business';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import {
  getMessagesClientsFallbackName,
  resolveMessagesClientsTemplate,
  type MessagesClientsTemplateKey,
} from '../utils/i18n/messagesClientsTemplates';

interface CreateTransactionFollowUpMessageParams {
  businessId: string;
  transactionGroupId: string;
  receiptNumber: string;
  clientId?: string;
  clientName: string;
  clientPhone?: string;
  clientOptIn?: boolean;
  clientLoyaltyBalance?: number;
  hasServiceItems: boolean;
  hasProductItems: boolean;
  transactionAt: Date;
  createdBy: string;
  config: {
    delayMinutes?: number;
    antiSpamHours?: number;
    requireOptIn?: boolean;
    templates?: MessagesClientsTemplatesConfiguration;
  };
}

const toDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return undefined;
};

const getTransactionType = (
  hasServiceItems: boolean,
  hasProductItems: boolean,
): 'services' | 'products' | 'mixed' => {
  if (hasServiceItems && hasProductItems) return 'mixed';
  if (hasServiceItems) return 'services';
  return 'products';
};

const LOYALTY_LINE_RE = /cagnotte|loyalty wallet|wallet balance/i;

const replaceTemplateVariables = (
  template: string,
  clientName: string,
  loyaltyBalance?: number,
): string => {
  let message = template;
  const numericLoyalty = Number(loyaltyBalance ?? 0);
  const hasPositiveLoyalty = Number.isFinite(numericLoyalty) && numericLoyalty > 0;

  if (!hasPositiveLoyalty) {
    message = message
      .split('\n')
      .filter((line) => !line.includes('{loyaltyBalance}') && !LOYALTY_LINE_RE.test(line))
      .join('\n');
  }

  message = message
    .replace(/\{clientName\}/g, clientName || getMessagesClientsFallbackName())
    .replace(/\{loyaltyBalance\}/g, String(Math.max(0, Math.round(numericLoyalty))));

  return message
    .replace(/\uFFFD/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const buildFollowUpMessage = (
  clientName: string,
  transactionType: 'services' | 'products' | 'mixed',
  templates?: MessagesClientsTemplatesConfiguration,
  loyaltyBalance?: number,
): string => {
  const source = templates ?? {};
  const preferredKey: MessagesClientsTemplateKey =
    transactionType === 'services'
      ? 'services'
      : transactionType === 'products'
        ? 'products'
        : 'mixed';

  const storedForType =
    (preferredKey === 'services' ? source.services : undefined)
    || (preferredKey === 'products' ? source.products : undefined)
    || (preferredKey === 'mixed' ? source.mixed : undefined);

  const storedGeneric = source.generic;

  let selected: string;
  if (storedForType) {
    selected = resolveMessagesClientsTemplate(preferredKey, storedForType);
  } else if (storedGeneric) {
    selected = resolveMessagesClientsTemplate('generic', storedGeneric);
  } else {
    selected = resolveMessagesClientsTemplate(preferredKey);
  }

  return replaceTemplateVariables(selected, clientName, loyaltyBalance);
};

export const createTransactionFollowUpMessage = async ({
  businessId,
  transactionGroupId,
  receiptNumber,
  clientId,
  clientName,
  clientPhone,
  clientOptIn,
  clientLoyaltyBalance,
  hasServiceItems,
  hasProductItems,
  transactionAt,
  createdBy,
  config,
}: CreateTransactionFollowUpMessageParams): Promise<string | null> => {
  console.log('🛠️ createTransactionFollowUpMessage - input', {
    businessId,
    transactionGroupId,
    receiptNumber,
    clientId,
    clientName,
    hasClientPhone: !!(clientPhone && String(clientPhone).trim()),
    clientOptIn,
    hasServiceItems,
    hasProductItems,
    transactionAt,
    configDelayMinutes: config.delayMinutes,
    configAntiSpamHours: config.antiSpamHours,
    configRequireOptIn: config.requireOptIn,
  });

  if (!clientId) {
    console.log('⏭️ createTransactionFollowUpMessage - skipped: missing clientId');
    return null;
  }

  const delayMinutes = Math.max(1, config.delayMinutes ?? 15);
  const antiSpamHours = Math.max(1, config.antiSpamHours ?? 24);
  const requireOptIn = config.requireOptIn ?? true;
  const now = new Date();
  const eligibleAt = new Date(transactionAt.getTime() + delayMinutes * 60 * 1000);
  const cleanClientPhone = (clientPhone || '').trim();
  const transactionType = getTransactionType(hasServiceItems, hasProductItems);

  let status: TransactionFollowUpStatus = 'pending_delay';
  let skippedReason: string | undefined;

  if (!hasUsableClientPhone(cleanClientPhone)) {
    status = 'skipped_no_phone';
    skippedReason = 'Client sans numéro exploitable';
    console.log('⚠️ createTransactionFollowUpMessage - no usable phone', {
      clientId,
      cleanClientPhone,
    });
  } else if (requireOptIn && clientOptIn === false) {
    status = 'skipped_opt_out';
    skippedReason = 'Client opt-out';
    console.log('⚠️ createTransactionFollowUpMessage - opt-out blocked', {
      clientId,
      requireOptIn,
      clientOptIn,
    });
  } else {
    const sentQuery = query(
      collection(db, COLLECTIONS.transactionFollowUpMessages),
      where('businessId', '==', businessId),
      where('clientId', '==', clientId),
      where('status', '==', 'sent'),
    );
    const sentSnapshot = await getDocs(sentQuery).catch((error) => {
      handleFirestoreError(error, 'createTransactionFollowUpMessage - anti spam query');
      throw error;
    });
    const cutoff = new Date(now.getTime() - antiSpamHours * 60 * 60 * 1000);
    const hasRecentSent = sentSnapshot.docs.some((docSnap) => {
      const sentAt = toDate(docSnap.data().sentAt);
      return !!sentAt && sentAt >= cutoff;
    });
    if (hasRecentSent) {
      status = 'suppressed_24h';
      skippedReason = `Client deja contacte dans les ${antiSpamHours}h`;
      console.log('⚠️ createTransactionFollowUpMessage - anti-spam blocked', {
        clientId,
        antiSpamHours,
      });
    }
  }

  const payload = {
    businessId,
    transactionGroupId,
    receiptNumber,
    clientId,
    clientName: clientName || '',
    clientPhone: cleanClientPhone,
    transactionType,
    transactionAt: Timestamp.fromDate(transactionAt),
    eligibleAt: Timestamp.fromDate(eligibleAt),
    status,
    messageText: buildFollowUpMessage(clientName, transactionType, config.templates, clientLoyaltyBalance),
    ...(skippedReason ? { skippedReason } : {}),
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now),
    createdBy,
  };

  console.log('📝 createTransactionFollowUpMessage - writing document', {
    businessId,
    clientId,
    status,
    eligibleAt,
    transactionType: payload.transactionType,
  });
  const docRef = await addDoc(collection(db, COLLECTIONS.transactionFollowUpMessages), payload);
  console.log('✅ createTransactionFollowUpMessage - document created', {
    docId: docRef.id,
    status,
  });
  return docRef.id;
};

export const useTransactionFollowUpMessages = () => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['messagesClients', businessId],
    queryFn: async () => {
      if (!businessId) return [];
      console.log('📥 useTransactionFollowUpMessages - fetch start', { businessId });
      const q = query(
        collection(db, COLLECTIONS.transactionFollowUpMessages),
        where('businessId', '==', businessId),
      );
      const snapshot = await getDocs(q).catch((error) => {
        handleFirestoreError(error, 'useTransactionFollowUpMessages - main query');
        throw error;
      });
      console.log('📥 useTransactionFollowUpMessages - fetch done', {
        businessId,
        docsCount: snapshot.size,
      });
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          businessId: data.businessId,
          transactionGroupId: data.transactionGroupId,
          receiptNumber: data.receiptNumber,
          clientId: data.clientId,
          clientName: data.clientName || '',
          clientPhone: data.clientPhone || '',
          transactionType: data.transactionType || 'mixed',
          transactionAt: toDate(data.transactionAt) || new Date(),
          eligibleAt: toDate(data.eligibleAt) || new Date(),
          status: data.status || 'pending_delay',
          messageText: data.messageText || '',
          sentAt: toDate(data.sentAt),
          sentBy: data.sentBy,
          skippedReason: data.skippedReason,
          createdAt: toDate(data.createdAt) || new Date(),
          updatedAt: toDate(data.updatedAt) || new Date(),
        } as TransactionFollowUpMessage;
      });
    },
    enabled: !!businessId,
  });
};

export const useMarkTransactionFollowUpMessageAsSent = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (messageId: string) => {
      const ref = doc(db, COLLECTIONS.transactionFollowUpMessages, messageId);
      await updateDoc(ref, {
        status: 'sent',
        sentAt: Timestamp.now(),
        sentBy: userProfile?.id || 'unknown',
        updatedAt: Timestamp.now(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messagesClients', businessId] });
    },
  });
};

export const useSkipTransactionFollowUpMessage = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async ({ messageId, reason }: { messageId: string; reason?: string }) => {
      const ref = doc(db, COLLECTIONS.transactionFollowUpMessages, messageId);
      await updateDoc(ref, {
        status: 'cancelled',
        ...(reason ? { skippedReason: reason } : {}),
        updatedAt: Timestamp.now(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messagesClients', businessId] });
    },
  });
};

export const useMessagesClientsConfig = () => {
  const { currentBusiness } = useAuth();
  return getMessagesClientsConfiguration(currentBusiness);
};
